import React, { useState } from "react";
import { Loader2, ShieldCheck, ExternalLink, Copy, Check } from "lucide-react";

const INPUT = "w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-sm mono outline-none";

function SetupSteps({ scopes }) {
  const [copied, setCopied] = useState(false);
  function copyScopes() {
    navigator.clipboard?.writeText(scopes || "").then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); });
  }
  return (
    <div className="text-xs text-zinc-400 leading-relaxed space-y-1">
      <div className="text-zinc-300 font-semibold">One-time setup</div>
      <ol className="list-decimal pl-4 space-y-1">
        <li>Google Cloud project → enable <b>Admin SDK API</b> and <b>Site Verification API</b>.</li>
        <li>Create a <b>service account</b> → Keys → add JSON key → paste below. Copy its numeric <b>Client ID</b>.</li>
        <li><a href="https://admin.google.com/ac/owl/domainwidedelegation" target="_blank" rel="noreferrer" className="underline text-sky-300 hover:text-sky-100 inline-flex items-center gap-1">Admin console → Security → API controls → Domain-wide delegation <ExternalLink size={10} /></a> → <b>Add new</b> → paste the Client ID and these scopes:
          <div className="mt-1 flex items-center gap-2">
            <code data-testid="gws-scopes" className="mono text-[10px] bg-zinc-950 border border-zinc-800 rounded-sm px-2 py-1 break-all">{scopes}</code>
            <button onClick={copyScopes} className="text-zinc-400 hover:text-white shrink-0" title="Copy scopes">{copied ? <Check size={13} /> : <Copy size={13} />}</button>
          </div>
        </li>
        <li>Enter a <b>super-admin email</b> to impersonate (the service account acts as this admin).</li>
      </ol>
    </div>
  );
}

function TextField({ id, label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <div className="label-tiny mb-1.5">{label}</div>
      <input data-testid={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={INPUT} />
    </label>
  );
}

/** Setup checklist + service-account form ("Test & save"). */
export function GwsSettingsForm({ s }) {
  const { cfg, form, setField, busy, editing } = s;
  return (
    <div className="space-y-3">
      <SetupSteps scopes={cfg.scopes} />
      <label className="block">
        <div className="label-tiny mb-1.5">Service account JSON key</div>
        <textarea data-testid="gws-sa-json" value={form.saJson} onChange={(e) => setField("saJson", e.target.value)} rows={5} placeholder='{"type": "service_account", "project_id": "...", "private_key": "...", "client_email": "...@....iam.gserviceaccount.com", ...}' className="w-full bg-zinc-950 border border-zinc-800 rounded-sm px-3 py-2 text-[11px] mono outline-none" />
      </label>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <TextField id="gws-subject" label="Super-admin to impersonate" value={form.subject} onChange={(v) => setField("subject", v)} placeholder="admin@yourcompany.com" />
        <TextField id="gws-primary" label="Primary domain (auto-detected if blank)" value={form.primary} onChange={(v) => setField("primary", v)} placeholder="yourcompany.com" />
        <TextField id="gws-rua" label="DMARC reports mailbox (optional)" value={form.rua} onChange={(v) => setField("rua", v)} placeholder="dmarc@yourcompany.com" />
      </div>
      <div className="flex items-center gap-2">
        <button data-testid="gws-settings-save" onClick={s.save} disabled={busy || !form.saJson.trim() || !form.subject.trim()} className="inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white px-4 py-2 rounded-sm text-sm font-semibold disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Test &amp; save
        </button>
        {editing && <button onClick={s.cancelEdit} className="text-zinc-400 hover:text-white text-sm px-3 py-2">Cancel</button>}
        <span className="text-[11px] text-zinc-500">The key is probed (lists your Workspace domains) before anything is stored. Never sent to the browser again.</span>
      </div>
    </div>
  );
}
